import { useEffect, useState } from 'react';
import { subscriptionApi, type Plan } from '../services/subscriptionApi';
import { formatAudMinor, seatTotalMinor } from '../utils/seatPricing';

/**
 * The live Stripe plans, for anything that needs to show a price.
 *
 * Fetched once per page load and shared: the sidebar renders on every screen, and each mount
 * re-requesting the same anonymous list would be pure waste. A failure resolves to an empty list
 * rather than throwing — a caller with no plan simply shows no price, which is the only safe
 * fallback. SeatPricing.cs is explicit that the client must not carry its own price table, so
 * "no price" is correct where "$29.99" would be a guess.
 */
let plansPromise: Promise<Plan[]> | null = null;

/** Drops the cached list. Tests call this so one case's stub cannot leak into the next. */
export function resetPlansCache(): void {
    plansPromise = null;
}

function loadPlans(): Promise<Plan[]> {
    plansPromise ??= subscriptionApi.getPlans()
        // Guarded, not merely typed: `getPlans` is typed as Plan[] but that is a promise about the
        // wire, not a fact. Anything else — an error object, a gateway's HTML, `{}` — reaches
        // `plans.find(...)` in the sidebar, which renders on EVERY page, so one malformed response
        // takes the whole app down rather than costing a price label.
        .then((plans) => (Array.isArray(plans) ? plans : []))
        .catch(() => []);
    return plansPromise;
}

export function usePlans(): Plan[] {
    const [plans, setPlans] = useState<Plan[]>([]);

    useEffect(() => {
        let live = true;
        loadPlans().then((p) => { if (live) setPlans(p); });
        return () => { live = false; };
    }, []);

    return plans;
}

/** What one plan's own line costs at `quantity` units, or null when that plan did not load. */
function lineTotalMinor(plan: Plan | undefined, quantity: number): number | null {
    if (!plan || quantity < 1) return null;
    return seatTotalMinor(plan.unitAmountMinor, plan.tiersMode, plan.tiers, quantity);
}

/**
 * What a subscription costs per month for `seats` PEOPLE on `planKey`, in cents.
 *
 * <p>The client mirror of SubscriptionShape.cs, and it has to be: a subscription is a base line at
 * quantity 1 plus a $9.99 add-on line for everyone after the first, so summing one plan's tiers is
 * no longer the price of anything. Quoting the tiered line instead understates a three-seat account
 * by a cent and misdescribes what the invoice will say.</p>
 *
 * <p>Returns null when there is no live price to quote — the plan did not load, or more than one
 * seat was asked for with no add-on price configured. That second case matters: the base price is
 * a FLAT $29.99 per unit, so putting a head count on it would quote $59.98 for two seats, and the
 * server now REFUSES to build that subscription at all. Quoting a number nothing will charge is
 * worse than quoting nothing.</p>
 *
 * <p>"No price" is the only safe fallback; SeatPricing.cs forbids a second price table, and a guess
 * is how a customer is quoted one number and charged another.</p>
 */
export function subscriptionTotalMinor(plans: Plan[], planKey: string, seats: number): number | null {
    const plan = plans.find((p) => p.key === planKey);
    if (!plan) return null;

    const people = Math.max(1, Math.floor(seats));

    // Marketplace is $0 and exactly one unit whatever the head count — it is not seat-priced.
    if (planKey !== 'intelligence') return lineTotalMinor(plan, 1);

    const base = lineTotalMinor(plan, 1);
    if (base === null || people === 1) return base;

    // No add-on price: there is nowhere to put the extra people, and the server will refuse the
    // purchase for the same reason. Quote nothing rather than the flat base times a head count.
    const seatPlan = plans.find((p) => p.key === 'intelligenceSeat');
    if (!seatPlan) return null;

    const extra = lineTotalMinor(seatPlan, people - 1);
    return extra === null ? null : base + extra;
}

/**
 * What ONE more person costs from where they are, in cents — not a constant.
 *
 * A flat $9.99 at every step against the live add-on price — but derived rather than printed, so
 * that changing the price in Stripe changes the page, and so an unconfigured add-on shows nothing
 * instead of a figure nothing will charge.
 */
export function nextSeatMinor(plans: Plan[], seats: number): number | null {
    const here = subscriptionTotalMinor(plans, 'intelligence', seats);
    const oneMore = subscriptionTotalMinor(plans, 'intelligence', seats + 1);
    return here === null || oneMore === null ? null : oneMore - here;
}

/**
 * "$39.98" for `seats` seats on `planKey`, or null when there is no live price to quote.
 *
 * Seats are PEOPLE. Passing a council count here is the bug removed in 0d062917 — see
 * seatPricing.ts, which this defers to for the arithmetic.
 */
export function usePlanPrice(planKey: string, seats: number): string | null {
    const plans = usePlans();
    const total = subscriptionTotalMinor(plans, planKey, seats);
    return total === null ? null : formatAudMinor(total);
}
